import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Swords, ShieldQuestion } from "lucide-react";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, StatusChip, ConfidenceBadge, SeverityChip, PersonaStamp, LoadingState, OutcomeChip } from "@/components/app/primitives";

const PERSONA_COLS = [
  { code: "TP", key: "partner", name: "Transformation Partner", accent: "border-t-[#B01212]", fields: [
    ["business_implication", "Business implication"], ["decision_needed", "Executive decision needed"], ["governance", "Governance impact"], ["owner_path", "Sponsor / owner path"], ["risk_if_ignored", "Risk if ignored"],
  ]},
  { code: "DS", key: "strategist", name: "AI/Data Strategist", accent: "border-t-[#2AA7A1]", fields: [
    ["evidence_strength", "Evidence strength"], ["data_quality", "Data quality concerns"], ["method", "Analytical method"], ["confidence_level", "Confidence level"], ["measurement", "Measurement design"],
  ]},
  { code: "TO", key: "operator", name: "The Operator", accent: "border-t-[#B7C0CC]", fields: [
    ["operational_reality", "Operational reality"], ["frontline_friction", "Likely frontline friction"], ["customer_impact", "Customer impact"], ["execution_play", "Practical next action"],
  ]},
];

const CONSENSUS_FIELDS = [
  ["what_we_believe", "What we believe"], ["what_we_know", "What we know"], ["what_we_dont_know", "What we do not know yet"],
  ["ask_next", "What we should ask next"], ["first_5_days", "First 5 days"], ["pilot_30_day", "30-day pilot path"], ["decision_needed", "Leadership decision needed"],
];

const CHALLENGE_FIELDS = [
  ["what_could_make_this_wrong", "What could make this finding wrong?"], ["evidence_to_strengthen", "What evidence would strengthen it?"],
  ["evidence_to_disprove", "What evidence would disprove it?"], ["risk_of_acting_too_soon", "Risk of acting too soon"],
  ["risk_of_waiting", "Risk of waiting"], ["minimum_evidence_to_act", "Minimum evidence needed to act responsibly"],
];

export default function TriadReviewPage() {
  const { data: findings, loading } = useFetch("/findings");
  const location = useLocation();
  const [selectedId, setSelectedId] = useState(null);
  const [challengeMode, setChallengeMode] = useState(false);

  useEffect(() => {
    if (findings?.length && !selectedId) {
      setSelectedId(location.state?.findingId || findings[0].id);
    }
  }, [findings, selectedId, location.state]);

  if (loading) return <LoadingState message="Convening the consortium..." />;
  const finding = (findings || []).find((f) => f.id === selectedId);

  return (
    <div data-testid="triad-page">
      <PageHeader
        kicker="Scene 3A - Consortium view"
        title="Triad Review Room"
        subtitle="The three senior personas evaluate each priority finding, then converge on a consensus output. Challenge Mode forces the counterfactual discipline before anything becomes pilot-ready."
        right={
          <button onClick={() => setChallengeMode(!challengeMode)} data-testid="triad-challenge-mode-toggle"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors active:scale-[0.98] ${challengeMode ? "bg-[--amber] text-black" : "bg-[--shell-3] text-[--tx-2] border border-[--hairline] hover:text-[--tx-1] hover:bg-[#243041]"}`}>
            <Swords size={13} /> {challengeMode ? "Challenge Mode ON" : "Challenge the Finding"}
          </button>
        }
      />

      <div className="flex gap-2 overflow-x-auto thin-scroll pb-2 mb-4" data-testid="triad-finding-selector">
        {(findings || []).map((f) => (
          <button key={f.id} onClick={() => setSelectedId(f.id)} data-testid={`triad-finding-tab-${f.id}`}
            className={`shrink-0 rounded-lg border px-3 py-2 text-left transition-colors active:scale-[0.98] max-w-[230px] ${f.id === selectedId ? "border-[--six-crimson] bg-[--six-crimson-soft]" : "border-[--hairline] bg-[--panel-dark] hover:bg-[--shell-3]"}`}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="font-mono text-[10px] text-[--tx-3]">{f.id}</span>
              <span className="font-mono text-[10px] text-[#E86A6A]">{f.rcc}</span>
            </div>
            <div className="text-xs font-medium text-[--tx-1] leading-snug line-clamp-2">{f.title}</div>
          </button>
        ))}
      </div>

      {finding && (
        <motion.div key={finding.id + (challengeMode ? "-c" : "")} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.16 }}>
          <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-4 mb-4" data-testid="triad-finding-header">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <ConfidenceBadge level={finding.status} />
              <SeverityChip severity={finding.severity} />
              <StatusChip tone="neutral">{finding.category}</StatusChip>
              {finding.six_outcomes?.map((o) => <OutcomeChip key={o} outcome={o} />)}
              <span className="ml-auto font-mono text-[10px] text-[--tx-3]">source: {finding.agent_source}</span>
            </div>
            <h2 className="font-display text-base font-semibold text-[--tx-1] mb-1">{finding.title}</h2>
            <p className="text-[13px] text-[--tx-2] leading-relaxed">{finding.headline}</p>
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {finding.evidence?.map((e) => <span key={e} className="font-mono text-[10px] rounded bg-[--shell-3] border border-[--divider] px-1.5 py-0.5 text-[--tx-3]">{e}</span>)}
            </div>
          </div>

          {!challengeMode ? (
            <>
              <div className="grid lg:grid-cols-3 gap-4 mb-4" data-testid="triad-columns">
                {PERSONA_COLS.map((col) => (
                  <div key={col.key} className={`rounded-xl border border-[--hairline] border-t-2 ${col.accent} bg-[--panel-dark] p-4`} data-testid={`triad-column-${col.key}`}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <PersonaStamp code={col.code} size="sm" />
                      <h3 className="text-[13px] font-semibold text-[--tx-1]">{col.name}</h3>
                    </div>
                    <div className="space-y-3">
                      {col.fields.map(([key, label]) => finding.triad?.[col.key]?.[key] && (
                        <div key={key}>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">{label}</div>
                          <p className="text-xs text-[--tx-2] leading-relaxed">{finding.triad[col.key][key]}</p>
                        </div>
                      ))}
                      {col.key === "strategist" && finding.triad?.strategist?.required_fields && (
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Required fields / data gaps</div>
                          <div className="flex flex-wrap gap-1">{finding.triad.strategist.required_fields.map((f) => <span key={f} className="text-[10px] rounded bg-[--ai-soft] text-[--ai] border border-white/10 px-1.5 py-0.5">{f}</span>)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Panel title="Consensus output" testId="triad-consensus-panel"
                right={<StatusChip tone="crimson">Where the three lenses converge</StatusChip>}>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                  {CONSENSUS_FIELDS.map(([key, label]) => (
                    <div key={key} className={key === "decision_needed" ? "xl:col-span-3 rounded-lg border border-[--amber] bg-[--amber-soft] p-3" : ""}>
                      <div className={`text-[10px] font-mono uppercase tracking-widest mb-0.5 ${key === "decision_needed" ? "text-[--amber]" : "text-[--tx-3]"}`}>{label}</div>
                      <p className="text-xs text-[--tx-2] leading-relaxed">{finding.consensus?.[key]}</p>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          ) : (
            <Panel title="Advisor Challenge - discipline before action" testId="triad-challenge-panel"
              right={<StatusChip tone="risk"><ShieldQuestion size={11} className="mr-1" /> Counterfactual review</StatusChip>}>
              <div className="grid md:grid-cols-2 gap-4">
                {CHALLENGE_FIELDS.map(([key, label]) => (
                  <div key={key} className="rounded-lg bg-[--shell-2] border border-[--divider] p-3.5" data-testid={`challenge-field-${key}`}>
                    <div className="text-[11px] font-semibold text-[--amber] mb-1">{label}</div>
                    <p className="text-xs text-[--tx-2] leading-relaxed">{finding.challenge?.[key]}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[--tx-3] mt-4 leading-relaxed">
                SIX acts before perfect data - but never before this review. Every pilot-bound finding passes the challenge gate so recommendations survive contact with validated extracts.
              </p>
            </Panel>
          )}
        </motion.div>
      )}
    </div>
  );
}
